import {
  useMemo,
  FunctionComponent,
  useCallback,
  useRef,
  memo,
  useState,
  useEffect,
} from 'react'

import { RecommendedItem } from '@hooks/useRecommendations'
import useTracking from './useTracking'

import VideoRecommendationsComponent, * as VideoRecommendationsComponents from '@components/Video/VideoRecommendations'

import {
  UsePlatformSpecificPropsWebOutput,
  VideoPlayingStatus,
} from '../types/darkcove'

export type VideoRecommendationsProps = Pick<
  UsePlatformSpecificPropsWebOutput,
  | 'videoId'
  | 'videoPlayingStatus'
  | 'isBlickTV'
  | 'isOverlay'
  | 'videoRecommendations'
> & {
  playerRef: UsePlatformSpecificPropsWebOutput['ref']
  isInArticle?: boolean
  hasPlaylist?: boolean
  title: string
}

const arrayIncludesItem = (array: any[], item: any) => array?.indexOf(item) >= 0

const VideoRecommendations: FunctionComponent<VideoRecommendationsProps> = ({
  playerRef,
  videoId,
  videoPlayingStatus,
  isOverlay,
  isBlickTV,
  videoRecommendations,
  isInArticle,
  hasPlaylist,
  title,
}) => {
  const player = playerRef?.current?.player?.()
  const [impressionsOnPause, setImpressionsOnPause] = useState<string[]>([])
  const [impressionsOnEnd, setImpressionsOnEnd] = useState<string[]>([])

  const swiperRef = useRef<VideoRecommendationsComponents.SwiperClass | null>(
    null
  )
  const impressionTimeoutRef = useRef<ReturnType<typeof setTimeout>>()

  const trackingCallbacks = useTracking({
    videoPlayingStatus,
    videoId,
    isOverlay,
    title,
  })

  const videoRecommendationsData = useMemo(
    () => videoRecommendations || [],
    [videoRecommendations]
  )

  useEffect(() => {
    return () => {
      if (impressionTimeoutRef.current) {
        clearTimeout(impressionTimeoutRef.current)
      }
    }
  }, [])

  const getIndexPosition = useCallback(
    (articleId: any) =>
      articleId && videoRecommendationsData
        ? videoRecommendationsData?.findIndex(
            (item) => item?.articleId === articleId
          )
        : -1,
    [videoRecommendationsData]
  )

  const play = useCallback(() => {
    playerRef.current?.play?.()
  }, [playerRef])

  const handleOnClickItem = useCallback(
    (_id: string, recommendationData: RecommendedItem) => {
      trackingCallbacks.onVideoWidgetClick({
        indexPosition: getIndexPosition(recommendationData.articleId),
        videoId: recommendationData.articleId,
        videoTitle: recommendationData.title,
      })
    },
    [getIndexPosition, trackingCallbacks]
  )

  const trackItemImpression = useCallback(
    (recommendationData: RecommendedItem) => {
      const indexPosition = getIndexPosition(recommendationData.articleId)

      if (indexPosition !== -1) {
        trackingCallbacks.onVideoWidgetImpression({
          indexPosition: indexPosition,
          videoId: recommendationData.articleId,
          videoTitle: recommendationData.title,
        })

        trackingCallbacks.onVideoWidgetImpressionAureus({
          aureusOfferId: recommendationData.aureusOfferId,
          isRecommendation: true,
        })
      }
    },
    [getIndexPosition, trackingCallbacks]
  )

  const handleClickPlay = useCallback(() => {
    play()

    trackingCallbacks.onVideoWidgetAction({
      widgetAction: 'play',
    })
  }, [play, trackingCallbacks])

  const handleClickReplay = useCallback(() => {
    play()

    trackingCallbacks.onVideoWidgetAction({
      widgetAction: 'replay',
    })

    /**
     * In order to delay any false positives from impressions
     * that have already been tracked.
     */
    impressionTimeoutRef.current = setTimeout(() => {
      setImpressionsOnPause([])
      setImpressionsOnEnd([])
    }, 500)
  }, [play, trackingCallbacks])

  const handleClickClose = useCallback(() => {
    playerRef?.current?.closeOverlay?.()
    trackingCallbacks.onVideoWidgetAction({
      widgetAction: 'close',
    })
  }, [playerRef, trackingCallbacks])

  /**
   * Every impression is tracked only once but separately between
   * onPause and onEnd. When the user replays, the state is cleared
   * in handleClickReplay() and then it can be tracked again once more
   */
  const handleItemImpression = useCallback(
    (recommendationsData: RecommendedItem) => {
      if (videoPlayingStatus === 'paused') {
        if (
          !arrayIncludesItem(impressionsOnPause, recommendationsData.articleId)
        ) {
          setImpressionsOnPause((oldArray) => [
            ...oldArray,
            recommendationsData.articleId,
          ])
          trackItemImpression(recommendationsData)
        }
      } else {
        if (
          !arrayIncludesItem(impressionsOnEnd, recommendationsData.articleId)
        ) {
          setImpressionsOnEnd((oldArray) => [
            ...oldArray,
            recommendationsData.articleId,
          ])
          trackItemImpression(recommendationsData)
        }
      }
    },
    [
      videoPlayingStatus,
      trackItemImpression,
      impressionsOnEnd,
      impressionsOnPause,
    ]
  )

  const handleOnSliderMove = useCallback(() => {}, [])

  const handleOnNavigationChange = useCallback(() => {}, [])

  return !isBlickTV &&
    videoPlayingStatus &&
    videoRecommendationsData?.length ? (
    <VideoRecommendationsComponent
      swiperRef={swiperRef}
      playerRef={playerRef}
      hidden={
        !(
          // we use `player.scrubbing()` to detect if the user is moving the cursor on timeline
          (
            (!player?.scrubbing?.() &&
              videoPlayingStatus === VideoPlayingStatus.PAUSED) ||
            (hasPlaylist
              ? false
              : videoPlayingStatus === VideoPlayingStatus.ENDED)
          )
        )
      }
      recommendationsTitle="Schau Dir weitere Videos an"
      showReplayButton={videoPlayingStatus === VideoPlayingStatus.ENDED}
      data={videoRecommendationsData}
      onClickItem={handleOnClickItem}
      onClickClose={handleClickClose}
      onClickPlay={handleClickPlay}
      onClickReplay={handleClickReplay}
      onNavigationChange={handleOnNavigationChange}
      onSliderMove={handleOnSliderMove}
      onItemImpression={handleItemImpression}
      smallSize={!isOverlay && (isInArticle || hasPlaylist)}
      isOverlay={isOverlay}
      videoPlayingStatus={videoPlayingStatus}
    />
  ) : null
}

const MemoizedVideoRecommendations = memo(VideoRecommendations)

MemoizedVideoRecommendations.displayName = 'MemoizedVideoRecommendations'

export default MemoizedVideoRecommendations
